/**
 * Estado y builders del formulario de Locación (US-2.2, TG-123).
 *
 * Compartido por el wizard de alta (`LocationForm`) y el modal de edición
 * (`LocationFormModal`), que reusan los mismos grupos de campos y estas funciones para
 * componer el POST/PATCH. Las reglas de validación viven en los grupos de campos.
 */
import type { CreateLocationBody, UpdateLocationBody } from '@goelectric/shared-types';

/** Tope de aliados traídos para el selector. Mismo criterio que el listado/detalle. */
export const ALLY_OPTIONS_LIMIT = 100;

/**
 * Forma del formulario (plano: lat/long separadas, `state` = departamento).
 *
 * `country`/`timeZone` no están: la plataforma es Colombia-only (`CO` fijo en el alta) y
 * hay una sola zona horaria, así que el backend aplica sus defaults en vez de tipearlos.
 */
export interface LocationFormState {
  name: string;
  address?: string;
  postalCode?: string;
  city?: string;
  state?: string;
  latitude?: number | null;
  longitude?: number | null;
  allyId?: string;
}

/** `''`/espacios → `undefined`, para no mandar campos vacíos en el PATCH. */
function trimOrUndefined(value?: string): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

/**
 * Cuerpo del POST. La validación del Form garantiza `name`/`address`/`city`/`state` y las
 * coordenadas; si faltara algo de eso devuelve `null` (defensivo). `state` (departamento)
 * es **requerido por el contrato**, así que siempre viaja. `country` va fijo en `CO`
 * (plataforma Colombia-only) y no se manda `timeZone` (el backend usa su default).
 *
 * `allyId` es opcional y **no nullable** en el alta (`type: string`): vacío = sin aliado
 * (caso mayoritario: pool que luego se ofrece a los aliados), así que se omite.
 */
export function buildCreateBody(values: LocationFormState): CreateLocationBody | null {
  if (typeof values.latitude !== 'number' || typeof values.longitude !== 'number') {
    return null;
  }
  const state = trimOrUndefined(values.state);
  if (!state) {
    return null;
  }
  const body: CreateLocationBody = {
    name: values.name.trim(),
    address: values.address?.trim() ?? '',
    city: values.city?.trim() ?? '',
    state,
    country: 'CO',
    coordinates: { latitude: values.latitude, longitude: values.longitude },
  };
  // `postalCode` es opcional (OCPI): vacío = se omite, no bloquea el alta.
  const postalCode = trimOrUndefined(values.postalCode);
  if (postalCode) body.postalCode = postalCode;
  const allyId = trimOrUndefined(values.allyId);
  if (allyId) body.allyId = allyId;
  return body;
}

/**
 * Cuerpo del PATCH. `name`/`address`/`city`/`postalCode` siempre viajan (incluso `''`): el
 * modal de edición es un formulario completo y envía el set entero del form, no un patch de
 * campos sueltos (funciona con el modal actual; si algún día se vuelve un patch realmente
 * parcial, acotar esos cuatro a "sólo con valor"). No manda `country` ni `timeZone` (no se
 * editan en el form; el PATCH es parcial y no hay que pisarlos).
 *
 * `allyId` SÍ es nullable en el contrato: limpiar el aliado (Select "Sin aliado") manda
 * `allyId: null`, que el backend traduce a "vuelve la Location al tenant de plataforma". Si
 * la locación ya no tenía aliado, el `null` es un no-op en el backend (no hay cambio que
 * trasladar).
 */
export function buildUpdateBody(values: LocationFormState): UpdateLocationBody {
  const body: UpdateLocationBody = {
    name: values.name.trim(),
    address: values.address?.trim() ?? '',
    city: values.city?.trim() ?? '',
    // `postalCode` viaja siempre (incluso `''`): el modal reusa los mismos campos y limpiar
    // el código postal debe poder borrarlo, no dejarlo pegado (el PATCH es parcial por
    // campo y sólo pisa lo que llega).
    postalCode: values.postalCode?.trim() ?? '',
  };
  const state = trimOrUndefined(values.state);
  const allyId = trimOrUndefined(values.allyId);
  if (state) body.state = state;
  body.allyId = allyId ?? null;
  if (typeof values.latitude === 'number' && typeof values.longitude === 'number') {
    body.coordinates = { latitude: values.latitude, longitude: values.longitude };
  }
  return body;
}
